import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, Autoplay } from 'swiper/modules'
import parse from 'html-react-parser'
import { useAuth } from '../context/AuthContext'
import { homeApi } from '../api/home'
import { coursesApi } from '../api/courses'
import { chatApi } from '../api/chat'
import { progressApi } from '../api/progress'
import { getStorageUrl } from '../utils/constants'
import { useScrollAnimation } from '../hooks/useScrollAnimation'
import Header from '../components/layout/Header'
import Footer from '../components/layout/Footer'
import Loading from '../components/common/Loading'
import CourseCard from '../components/course/CourseCard'
import Button from '../components/common/Button'
import YouTubeModal from '../components/common/YouTubeModal'
import SEO from '../components/seo/SEO'
import { generateWebsiteStructuredData, generateOrganizationStructuredData } from '../utils/seo'
import { FiPlay, FiBookOpen, FiStar, FiDownload, FiHeadphones, FiSmartphone, FiCode, FiBriefcase, FiTrendingUp, FiLayers, FiCamera, FiMusic, FiHeart, FiZap, FiTarget, FiAward, FiGlobe, FiCpu, FiPieChart, FiBarChart, FiDollarSign, FiShield, FiUsers, FiShoppingBag, FiHome, FiCoffee, FiMapPin } from 'react-icons/fi'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import './Home.css'

// Professional icon mapping for subjects
const getSubjectIcon = (subjectName) => {
  const name = (subjectName || '').toLowerCase()
  const iconMap = {
    'برمجة': FiCode,
    'تطوير': FiCode,
    'برمجيات': FiCode,
    'تقنية': FiCpu,
    'تكنولوجيا': FiCpu,
    'إدارة': FiBriefcase,
    'أعمال': FiBriefcase,
    'تجارة': FiShoppingBag,
    'اقتصاد': FiDollarSign,
    'مالية': FiDollarSign,
    'تسويق': FiTrendingUp,
    'تصميم': FiLayers,
    'جرافيك': FiLayers,
    'تصوير': FiCamera,
    'فوتوغرافيا': FiCamera,
    'موسيقى': FiMusic,
    'صحة': FiHeart,
    'طب': FiHeart,
    'لياقة': FiZap,
    'رياضة': FiZap,
    'تعليم': FiBookOpen,
    'لغة': FiGlobe,
    'لغات': FiGlobe,
    'إنجليزي': FiGlobe,
    'عربي': FiBookOpen,
    'رياضيات': FiPieChart,
    'إحصاء': FiBarChart,
    'أمن': FiShield,
    'أمان': FiShield,
    'مجتمع': FiUsers,
    'علوم': FiAward,
    'هندسة': FiTarget,
    'عمارة': FiHome,
    'طبخ': FiCoffee,
  }
  
  for (const [key, Icon] of Object.entries(iconMap)) {
    if (name.includes(key)) {
      return Icon
    }
  }
  return FiBookOpen // Default icon
}

// Professional color palette for subjects
const subjectColors = [
  { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', icon: '#667eea' },
  { bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', icon: '#f5576c' },
  { bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', icon: '#4facfe' },
  { bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', icon: '#43e97b' },
  { bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', icon: '#fa709a' },
  { bg: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', icon: '#30cfd0' },
  { bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', icon: '#a8edea' },
  { bg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', icon: '#ff9a9e' },
  { bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', icon: '#fcb69f' },
  { bg: 'linear-gradient(135deg, #ff8a80 0%, #ea6100 100%)', icon: '#ff8a80' },
]

// Helper function to get subject metadata (university, college, band)
const getSubjectMetadata = (subject) => {
  const metadata = {}
  
  // Try multiple possible property names for university
  const universityName = 
    subject.university?.name || 
    subject.university_name || 
    subject.university?.title ||
    (subject.university && typeof subject.university === 'string' ? subject.university : null)
  
  if (universityName) {
    metadata.university = universityName
  }
  
  // Try multiple possible property names for college
  const collegeName = 
    subject.college?.name || 
    subject.college_name || 
    subject.college?.title ||
    (subject.college && typeof subject.college === 'string' ? subject.college : null)
  
  if (collegeName) {
    metadata.college = collegeName
  }
  
  // Try multiple possible property names for university band
  const bandName = 
    subject.university_band?.name || 
    subject.university_band_name || 
    subject.university_band?.title ||
    subject.band?.name || 
    subject.band_name ||
    subject.band?.title ||
    (subject.university_band && typeof subject.university_band === 'string' ? subject.university_band : null) ||
    (subject.band && typeof subject.band === 'string' ? subject.band : null)
  
  if (bandName) {
    metadata.band = bandName
  }
  
  return metadata
}

// Helper function to detect HTML content
const containsHtml = (text) => {
  if (typeof text !== 'string') return false
  const htmlPattern = /<[^>]+>/
  return htmlPattern.test(text)
}

// FAQ Accordion Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false)
  const isHtml = containsHtml(answer)
  
  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 text-right flex items-center justify-between hover:bg-gray-700 transition-colors"
      >
        <span className="text-white font-bold text-lg">{question}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`text-primary-blue transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}
        >
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
      </button>
      {isOpen && (
        <div className="px-6 pb-6 text-gray-400 leading-relaxed">
          {isHtml ? (
            <div className="faq-answer-html-content">
              {parse(answer)}
            </div>
          ) : (
            answer
          )}
        </div>
      )}
    </div>
  )
}

// Counter Animation Component
const Counter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0)
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.5, triggerOnce: true })

  useEffect(() => {
    if (!isVisible) return

    let startTime
    let animationFrame

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp
      const progress = timestamp - startTime
      const percentage = Math.min(progress / duration, 1)
      
      // Easing function (easeOutExpo)
      const ease = 1 - Math.pow(2, -10 * percentage)
      
      setCount(Math.floor(end * ease))

      if (progress < duration) {
        animationFrame = requestAnimationFrame(animate)
      } else {
        setCount(end)
      }
    }

    animationFrame = requestAnimationFrame(animate)

    return () => cancelAnimationFrame(animationFrame)
  }, [isVisible, end, duration])

  return <span ref={ref}>{count}{suffix}</span>
}

// Typing Animation Component
const TypingText = ({ text, className, speed = 100, delay = 0, trigger = true }) => {
  const [displayedText, setDisplayedText] = useState('')
  const [isTyping, setIsTyping] = useState(false)

  useEffect(() => {
    if (!trigger) return

    let timeoutId
    let intervalId

    timeoutId = setTimeout(() => {
      setIsTyping(true)
      let index = 0
      intervalId = setInterval(() => {
        if (index <= text.length) {
          setDisplayedText(text.slice(0, index))
          index++
        } else {
          clearInterval(intervalId)
          setIsTyping(false)
        }
      }, speed)
    }, delay)

    return () => {
      clearTimeout(timeoutId)
      clearInterval(intervalId)
    }
  }, [text, speed, delay, trigger])

  return (
    <span className={`${className} ${isTyping ? 'typing-cursor' : ''}`}>
      {displayedText}
    </span>
  )
}

// Animated Section Component for dynamic sections
const AnimatedSection = ({ children, className, id, animation = 'animate-fade-up' }) => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0, rootMargin: '100px' })
  const [forceShow, setForceShow] = useState(false)

  // Safety fallback: ensure content is visible after a short delay if animation doesn't trigger
  useEffect(() => {
    const timer = setTimeout(() => {
      setForceShow(true)
    }, 1000)
    return () => clearTimeout(timer)
  }, [])

  const shouldShow = isVisible || forceShow

  return (
    <section 
      ref={ref} 
      id={id} 
      className={`${className} ${shouldShow ? animation : 'section-waiting'}`}
      style={{ minHeight: '200px' }} // Ensure section has height for observer
    >
      {children}
    </section>
  )
}

const Home = () => {
  const { isAuthenticated, user } = useAuth()
  const queryClient = useQueryClient()
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)

  // Scroll animation hooks for different sections
  const [continueRef, isContinueVisible] = useScrollAnimation({ threshold: 0.1, rootMargin: '50px' })
  const [instructorsRef, isInstructorsVisible] = useScrollAnimation({ threshold: 0.1, rootMargin: '50px' })
  const [reelsRef, isReelsVisible] = useScrollAnimation({ threshold: 0.1, rootMargin: '50px' })
  const [storiesRef, isStoriesVisible] = useScrollAnimation({ threshold: 0.1, rootMargin: '50px' })
  const [appDownloadRef, isAppDownloadVisible] = useScrollAnimation({ threshold: 0.1, rootMargin: '50px' })
  const [subjectsRef, isSubjectsVisible] = useScrollAnimation({ threshold: 0.1, rootMargin: '50px' })
  const [telegramRef, isTelegramVisible] = useScrollAnimation({ threshold: 0.1, rootMargin: '50px' })
  const [achievementsRef, isAchievementsVisible] = useScrollAnimation({ threshold: 0.1, rootMargin: '50px' })
  const [whyRef, isWhyVisible] = useScrollAnimation({ threshold: 0.1, rootMargin: '50px' })
  const [faqRef, isFaqVisible] = useScrollAnimation({ threshold: 0.1, rootMargin: '50px' })
  const [ctaRef, isCtaVisible] = useScrollAnimation({ threshold: 0.1, rootMargin: '50px' })
  const [entertainmentRef, isEntertainmentVisible] = useScrollAnimation({ threshold: 0.1, rootMargin: '50px' })
  
  // Try to fetch home data (may require auth)
  const { data, isLoading, error } = useQuery({
    queryKey: ['home'],
    queryFn: () => homeApi.getHomeData(),
    retry: false,
    // Don't fail if API requires auth - show content anyway
  })

  // Fallback: fetch courses if home API fails
  const { data: coursesData } = useQuery({
    queryKey: ['courses', 'featured'],
    queryFn: () => coursesApi.getCourses({ per_page: 6 }),
    enabled: !data && !isLoading, // Only fetch if home failed
    retry: false,
  })

  // Fetch knowledge base questions for FAQ section
  const locale = localStorage.getItem('locale') || 'ar'
  const { data: knowledgeBaseQuestions = [], isLoading: kbLoading } = useQuery({
    queryKey: ['knowledge-base-questions-faq', locale],
    queryFn: () => chatApi.getKnowledgeBaseQuestions('website', locale),
    retry: false,
  })

  // Get data or use empty object
  const homeData = data?.data || {}
  homeData.success_stories =[
    {
      id: 1,
      name: 'طالب متفوق',
      story: 'انا مجربتش الأونلاين قبل كدة خالص علشان ما بفهمش منه بس جربت معاكم عرض المراجعات قولت اجرب وبجد مش مصدقة اني حليت كل الامتحانات صح 😂❤️ ولميت الدنيا بعد ما كنت مش عارفة اي حاجة بجد ربنا يباركلكم ❤️❤️',
    },
    {
      id: 2,
      name: 'ولي أمر طالب',
      story: 'ابني طالب دمج وكان موضوع الحضور صعب جداً بالنسباله وحتى الأونلاين بيكون صعب فلازم حد يقعد معاه ويشرحله بس حقيقي لما اشتركت معاكم وبدأت اسمع معاه المحاضرات واتابعه واحل معاه لقينا الموضوع كويس وبيستوعب بسرعة والحمد لله نجح بفضل الله ثم المنصة الجميلة 🤍',
    },
    {
      id: 3,
      name: 'طالبة جامعية',
      story: 'شكراً كتير لحضراتكم انا والله معتمدة على المنصة ومش بنزل الجامعة خالص ومبسوطة اني قدرت اجيب درجات حلوة ❤️❤️',
    },
  ];
  const fallbackCourses = coursesData?.data?.courses || coursesData?.data || []

  // Fetch progress for continue learning course if available
  const continueLearningCourseId = homeData.continue_learning?.course?.id
  const { data: courseProgressData, isLoading: progressLoading } = useQuery({
    queryKey: ['course-progress', continueLearningCourseId],
    queryFn: () => {
      if (!continueLearningCourseId) return null
      return progressApi.getCourseProgress(continueLearningCourseId)
    },
    enabled: !!continueLearningCourseId && isAuthenticated,
    retry: false,
    refetchInterval: 10000, // Refetch every 10 seconds for real-time updates
    refetchOnWindowFocus: true, // Refetch when user returns to the tab
    refetchOnMount: true, // Refetch when component mounts
  })
  
  // Refetch progress when courseId becomes available
  useEffect(() => {
    if (continueLearningCourseId && isAuthenticated) {
      queryClient.invalidateQueries({ queryKey: ['course-progress', continueLearningCourseId] })
    }
  }, [continueLearningCourseId, isAuthenticated, queryClient])
  
  const [phoneActiveSlide, setPhoneActiveSlide] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setPhoneActiveSlide(prev => (prev === 0 ? 1 : 0))
    }, 5000) // Change every 5 seconds
    return () => clearInterval(interval)
  }, [])

  // Show loading only on first load
  if (isLoading && !data) {
    return (
      <>
        <Header />
        <Loading fullScreen text="جاري التحميل..." />
        <Footer />
      </>
    )
  }

  // Determine which courses to show
  // Priority: 1. free_courses from API, 2. courses from sections, 3. fallback courses
  const coursesFromSections = homeData.sections
    ?.filter(s => s.content_type === 'courses')
    .flatMap(s => s.content || [])
    .filter(Boolean) || []
  
  const coursesToShow = homeData.free_courses && homeData.free_courses.length > 0 
    ? homeData.free_courses 
    : (coursesFromSections.length > 0 
      ? coursesFromSections 
      : (Array.isArray(fallbackCourses) ? fallbackCourses : []))

  // Get reels data - prioritize section content, fallback to direct reels
  const reelsSection = homeData.sections?.find(s => s.content_type === 'reels')
  const reelsFromSection = reelsSection?.content || []
  const reels = homeData.reels || []
  const allReels = reelsFromSection.length > 0 ? reelsFromSection : reels
  
  // Get instructors from sections
  const instructorsSection = homeData.sections?.find(s => s.content_type === 'instructors')
  const instructors = instructorsSection?.content || []
  
  // Get all courses from sections for display (not just free_courses)
  const allCoursesFromSections = homeData.sections
    ?.filter(s => s.content_type === 'courses')
    .flatMap(s => s.content || [])
    .filter(Boolean) || []

  const siteUrl = 'https://3afer.net'
  const websiteStructuredData = generateWebsiteStructuredData(siteUrl)
  const organizationStructuredData = generateOrganizationStructuredData(siteUrl)

  return (
    <div className="home min-h-screen bg-gray-900 font-sans text-white selection:bg-primary-blue selection:text-white">
      <SEO
        title="الصفحة الرئيسية"
        description="تعلّم أونلاين من أكبر مكتبة كورسات في الوطن العربي. دورات تعليمية احترافية في مختلف المجالات مع أفضل المدربين العرب."
        keywords="تعليم, دورات, كورسات, أونلاين, تعليم إلكتروني, دورات عربية, تعلم مجاني, شهادات معتمدة"
        url="/"
        structuredData={[websiteStructuredData, organizationStructuredData]}
      />
      <Header />
      <main className="home-main">
        {/* Hero Section */}
        <header className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800">
          {/* Background Decor */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary-blue/10 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 -left-24 w-72 h-72 bg-accent-orange/10 rounded-full blur-3xl"></div>
            
            {/* Magic Particles */}
            <div className="magic-particles">
              {[...Array(20)].map((_, i) => (
                <div key={i} className="magic-particle" style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${3 + Math.random() * 5}s`
                }}></div>
              ))}
            </div>
          </div>

          <div className="container mx-auto px-4 relative z-10 grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-right space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 rounded-full shadow-sm border border-gray-700 text-white font-bold text-sm hero-animate-in">
                <span className="flex h-2 w-2 rounded-full bg-accent-orange"></span>
                منصة التعليم الأولى
              </div>
              <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-[1.2] hero-animate-in hero-delay-1">
                <TypingText text="منصتك الأولى للنجاح في التعليم" speed={50} delay={200} /> <br />
                <span className="text-primary-blue">
                  <TypingText text="مع أفضل الخبراء" speed={50} delay={2000} />
                </span>
              </h1>
              <p className="text-lg md:text-xl text-gray-300 max-w-xl leading-relaxed hero-animate-in hero-delay-2">
              منصة تعليمية متخصصة للتجاريين تهدف إلى تطوير مهارات الطلاب والخريجين بتوفير دورات تدريبية متكاملة بتقنية عالية ومحاضرين محترفين
              </p>
              <div className="flex flex-wrap gap-4 hero-animate-in hero-delay-3">
                <Link to="/my-courses">
                  <button 
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold transition-all duration-300 transform hover:-translate-y-1 active:scale-95 text-sm md:text-base text-white hover:bg-[#0052a3] shadow-lg hover:shadow-blue-500/30 flex-row-reverse"
                    style={{ backgroundColor: 'rgb(0, 102, 204)' }}
                  >
                    تصفح الدورات
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m12 19-7-7 7-7"/>
                      <path d="M19 12H5"/>
                    </svg>
                  </button>
                </Link>
                <Link to="/register">
                  <button 
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold transition-all duration-300 transform hover:-translate-y-1 active:scale-95 text-sm md:text-base bg-gray-800 text-white hover:bg-gray-700 shadow-md border border-gray-700"
                  >
                    أنشئ حسابك الآن
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14"/>
                      <path d="m12 5 7 7-7 7"/>
                    </svg>
                  </button>
                </Link>
              </div>
              
              <div className="pt-8 flex items-center gap-6 text-sm font-medium text-gray-500">
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent-orange">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <path d="M9 11l3 3L22 4"/>
                  </svg>
                  <span>خدمة تعليمية متكاملة</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent-orange">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <path d="M9 11l3 3L22 4"/>
                  </svg>
                  <span>دعم فني 24/7</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent-orange">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <path d="M9 11l3 3L22 4"/>
                  </svg>
                  <span>نخبة المحاضرين</span>
                </div>
              </div>
            </div>

            <div className="relative hidden lg:block hero-animate-scale hero-delay-2">
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl border-4 border-white transform rotate-2 hover:rotate-0 transition-transform duration-500">
                {homeData.banners && homeData.banners.length > 0 ? (
                  <Swiper
                    modules={[Navigation, Pagination, Autoplay]}
                    spaceBetween={0}
                    slidesPerView={1}
                    navigation
                    pagination={{ clickable: true }}
                    autoplay={{
                      delay: 5000,
                      disableOnInteraction: false,
                    }}
                    loop={homeData.banners.length > 1}
                    className="w-full h-auto"
                  >
                    {homeData.banners.map((banner, bannerIndex) => (
                      <SwiperSlide key={banner.id != null ? `banner-${banner.id}-${bannerIndex}` : `banner-${bannerIndex}`}>
                        <img 
                          src={getStorageUrl(banner.image_url || banner.image)} 
                          alt={banner.title} 
                          className="w-full h-auto object-cover"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                ) : (
                  <img 
                    src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                    alt="Student learning" 
                    className="w-full h-auto object-cover"
                  />
                )}
                
                {/* Floating Badge */}
                <div className="absolute bottom-8 right-8 bg-gray-900 p-4 rounded-xl shadow-lg flex items-center gap-4 animate-bounce-slow border border-gray-700">
                  <div className="w-12 h-12 bg-primary-blue/20 text-primary-blue rounded-full flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">+5000</div>
                    <div className="text-xs text-gray-400">طالب نشط</div>
                  </div>
                </div>
              </div>
              {/* Decoration Pattern */}
              <div className="absolute -bottom-10 -left-10 w-full h-full border-2 border-accent-orange rounded-3xl z-0"></div>
            </div>
          </div>
        </header>

        {/* Telegram Channel Section */}
        <section ref={telegramRef} className={`py-16 bg-gray-800 ${isTelegramVisible ? 'animate-zoom-in' : 'section-waiting'}`}>
          <div className="container mx-auto px-4">
            <div className="bg-gray-900 rounded-3xl p-8 md:p-12 border border-gray-700">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
                  <TypingText text="إنضم إلى جروباتنا" speed={50} delay={200} trigger={isTelegramVisible} />
                </h2>
                <p className="text-gray-400 text-lg">متابعة أولية، تحفيز دائم، ومسابقات ممتعة مع جوائز وتنبيهات وملخصات مرئية</p>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 mb-12 stagger-children">
                <div className="text-center p-4 bg-gray-800 rounded-xl border border-gray-700">
                  <div className="text-3xl font-extrabold text-primary-blue mb-2">
                    <Counter end={50} suffix="k" />
                  </div>
                  <div className="text-gray-400 text-sm">مشترك</div>
                </div>
                <div className="text-center p-4 bg-gray-800 rounded-xl border border-gray-700">
                  <div className="text-3xl font-extrabold text-primary-blue mb-2">
                    <Counter end={200} suffix="+" />
                  </div>
                  <div className="text-gray-400 text-sm">مسابقة</div>
                </div>
                <div className="text-center p-4 bg-gray-800 rounded-xl border border-gray-700">
                  <div className="text-3xl font-extrabold text-primary-blue mb-2">
                    <Counter end={99} suffix="+" />
                  </div>
                  <div className="text-gray-400 text-sm">كورس</div>
                </div>
              </div>

              {/* Features Grid */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-white mb-6 text-center">ماذا ستحصل عليه؟</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 stagger-children">
                  {[
                    { icon: '📋', title: 'متابعة أولية', desc: 'خطط متابعة وبداية التعلم' },
                    { icon: '💬', title: 'رسايل تشجيعية', desc: 'تحفيز مستمر ودعم معنوي' },
                    { icon: '🏆', title: 'مسابقات واختبارات', desc: 'اختبر معلوماتك وتحدى نفسك' },
                    { icon: '🎁', title: 'جوايز ومكافات', desc: 'احصل على جوائز قيمة' },
                    { icon: '🔔', title: 'تنبيهات', desc: 'تذكيرات وتنبيهات مهمة' },
                    { icon: '📹', title: 'فيديوهات وملخصات', desc: 'ملخصات مرئية سهلة الفهم' },
                  ].map((feature, idx) => (
                    <div key={idx} className="p-4 bg-gray-800 rounded-xl border border-gray-700 hover:border-primary-blue transition-colors">
                      <div className="text-3xl mb-2">{feature.icon}</div>
                      <h4 className="font-bold text-white mb-1">{feature.title}</h4>
                      <p className="text-gray-400 text-sm">{feature.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <div className="text-center">
                <a 
                  href="https://linktr.ee/3afer1" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold transition-all duration-300 transform hover:-translate-y-1 active:scale-95 text-white shadow-lg hover:shadow-blue-500/30"
                  style={{ backgroundColor: '#0066CC' }}
                >
                  انضم للقناة الآن
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m12 19-7-7 7-7"/>
                    <path d="M19 12H5"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Achievements Section */}
        <section ref={achievementsRef} className={`py-16 bg-gray-800 ${isAchievementsVisible ? 'animate-bounce-up' : 'opacity-0'}`}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">إنجازاتنا</h2>
              <p className="text-gray-400">نفتخر بإنجازاتنا في دعم الطلاب والمعلمين لتحقيق أفضل النتائج.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 stagger-children">
              {/* Achievement Card 1 */}
              <div className="text-center p-6 bg-gray-900 rounded-2xl border border-gray-700 hover:border-primary-blue transition-all hover:shadow-lg hover:shadow-primary-blue/20">
                <div className="w-16 h-16 mx-auto bg-primary-blue/20 rounded-full flex items-center justify-center text-primary-blue mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div className="text-3xl font-extrabold text-white mb-1">+{homeData.total_students || '15k'}</div>
                <div className="text-gray-400 font-medium">طالب متفوق</div>
              </div>
              {/* Achievement Card 2 */}
              <div className="text-center p-6 bg-gray-900 rounded-2xl border border-gray-700 hover:border-primary-blue transition-all hover:shadow-lg hover:shadow-primary-blue/20">
                <div className="w-16 h-16 mx-auto bg-primary-blue/20 rounded-full flex items-center justify-center text-primary-blue mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 19V5a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2z"/>
                    <path d="M22 10a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2z"/>
                    <path d="M15 7h.01"/>
                  </svg>
                </div>
                <div className="text-3xl font-extrabold text-white mb-1">+{homeData.total_teachers || '50'}</div>
                <div className="text-gray-400 font-medium">معلم معتمد</div>
              </div>
              {/* Achievement Card 3 */}
              <div className="text-center p-6 bg-gray-900 rounded-2xl border border-gray-700 hover:border-primary-blue transition-all hover:shadow-lg hover:shadow-primary-blue/20">
                <div className="w-16 h-16 mx-auto bg-primary-blue/20 rounded-full flex items-center justify-center text-primary-blue mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 15a5 5 0 0 0-5 5"/>
                    <path d="M18.32 17.66A2 2 0 0 1 21 19v2"/>
                    <path d="M2.68 17.66A2 2 0 0 0 5 19v2"/>
                    <path d="m9 11 3-3 3 3"/>
                    <path d="M12 15V8"/>
                  </svg>
                </div>
                <div className="text-3xl font-extrabold text-white mb-1">+{homeData.total_courses || '120'}</div>
                <div className="text-gray-400 font-medium">كورس</div>
              </div>
              {/* Achievement Card 4 */}
              <div className="text-center p-6 bg-gray-900 rounded-2xl border border-gray-700 hover:border-primary-blue transition-all hover:shadow-lg hover:shadow-primary-blue/20">
                <div className="w-16 h-16 mx-auto bg-primary-blue/20 rounded-full flex items-center justify-center text-primary-blue mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                </div>
                <div className="text-3xl font-extrabold text-white mb-1">+{homeData.total_books * 20|| '0'}</div>
                <div className="text-gray-400 font-medium">كتاب</div>
              </div>
            </div>
          </div>
        </section>

        {/* Promotional Booking Section */}
        <section ref={ctaRef} className={`py-16 bg-gray-900 ${isCtaVisible ? 'animate-scale-up' : 'section-waiting'}`}>
          <div className="container mx-auto px-4">
            <div className="bg-gradient-to-r from-primary-blue to-primary-blue/80 rounded-3xl p-8 md:p-12 border-2 border-primary-blue/50 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
              <div className="relative z-10 text-center">
                <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">زمايلك كلهم في عافر وانت لسة!</h2>
                <p className="text-gray-200 text-lg mb-8 max-w-2xl mx-auto">
                سجّل دلوقتي وابدأ رحلتك التعليمية مع منصة عافر التعليمية واضمن تقديرك ووفر وقتك وفلوسك
                </p>
                <Link to="/register">
                  <button 
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold transition-all duration-300 transform hover:-translate-y-1 active:scale-95 text-white shadow-lg hover:shadow-green-500/30"
                    style={{ backgroundColor: '#10b981' }}
                  >
                    أحجز دلوقتي..
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m12 19-7-7 7-7"/>
                      <path d="M19 12H5"/>
                    </svg>
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Continue Learning Section */}
        {isAuthenticated && homeData.continue_learning && homeData.continue_learning.course && (
          <section 
            ref={continueRef}
            className={`section continue-learning-section ${isContinueVisible ? 'animate-fade-right' : 'section-waiting'}`}
          >
            <div className="container">
              <Link 
                to={`/courses/${homeData.continue_learning.course.id}/lectures/${homeData.continue_learning.last_lecture_id || ''}`}
                className="continue-learning-card"
              >
                <div className="continue-accent-border"></div>
                <div className="continue-content">
                  {homeData.continue_learning.course.thumbnail && (
                    <div className="continue-thumbnail-wrapper">
                      <img
                        src={getStorageUrl(homeData.continue_learning.course.thumbnail)}
                        alt={homeData.continue_learning.course.title}
                        className="continue-thumbnail"
                      />
                      <div className="continue-play-overlay">
                        <FiPlay className="play-icon" />
                      </div>
                    </div>
                  )}
                  <div className="continue-info">
                    <div className="continue-label">
                      <FiBookOpen /> تابع تعلمك
                    </div>
                    <h3 className="continue-title">{homeData.continue_learning.course.title}</h3>
                    {(() => {
                      // Use latest progress from API if available, otherwise use home data
                      const progressData = courseProgressData?.data
                      let displayProgress = progressData?.percentage ?? homeData.continue_learning?.progress ?? 0
                      const displayCompletedLectures = progressData?.completed_lectures ?? homeData.continue_learning?.completed_lectures ?? 0
                      const displayTotalLectures = progressData?.total_lectures ?? homeData.continue_learning?.total_lectures ?? 0
                      const watchTime = progressData?.watch_time ?? 0
                      const totalDuration = progressData?.total_duration ?? 0
                      
                      // If percentage is 0 but we have watch_time, calculate a fallback percentage
                      // This handles cases where videos don't have duration (e.g., HLS/Bunny videos)
                      if (displayProgress === 0 && watchTime > 0 && totalDuration === 0 && displayTotalLectures > 0) {
                        // Use completion-based calculation: if watch_time > 60 seconds, show at least 1% progress
                        // Otherwise calculate based on completed lectures only
                        if (displayCompletedLectures > 0) {
                          displayProgress = Math.round((displayCompletedLectures / displayTotalLectures) * 100)
                        } else if (watchTime >= 60) {
                          // Show minimal progress (1-5%) if user has watched at least 1 minute
                          // This gives visual feedback that progress exists
                          displayProgress = Math.min(5, Math.max(1, Math.round((watchTime / 3600) * 100))) // Rough estimate: 1 hour = 100%
                        }
                      }
                      
                      // Debug logging (remove in production)
                      if (process.env.NODE_ENV === 'development') {
                        console.log('Continue Learning Progress:', {
                          courseId: continueLearningCourseId,
                          progressData,
                          displayProgress,
                          displayCompletedLectures,
                          displayTotalLectures,
                          watchTime,
                          totalDuration,
                          homeProgress: homeData.continue_learning?.progress,
                        })
                      }
                      
                      return displayProgress !== undefined && (
                        <div className="continue-progress-section">
                          <div className="circular-progress-wrapper">
                            <svg className="circular-progress" viewBox="0 0 100 100">
                              <defs>
                                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" stopColor="#2473B4" />
                                  <stop offset="100%" stopColor="#EAA64D" />
                                </linearGradient>
                              </defs>
                              <circle
                                className="circular-progress-bg"
                                cx="50"
                                cy="50"
                                r="45"
                                fill="none"
                                strokeWidth="8"
                              />
                              <circle
                                className="circular-progress-fill"
                                cx="50"
                                cy="50"
                                r="45"
                                fill="none"
                                strokeWidth="8"
                                strokeDasharray={`${2 * Math.PI * 45}`}
                                strokeDashoffset={`${2 * Math.PI * 45 * (1 - Math.min(100, Math.max(0, displayProgress)) / 100)}`}
                              />
                            </svg>
                            <div className="circular-progress-text">
                              <span className="progress-percentage-large">
                                {Math.round(displayProgress)}%
                              </span>
                            </div>
                          </div>
                          <div className="progress-stats">
                            <div className="progress-stat-item">
                              <span className="stat-label">الدورات المكتملة</span>
                              <span className="stat-value">
                                {displayCompletedLectures} / {displayTotalLectures}
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    })()}
                  </div>
                </div>
              </Link>
            </div>
          </section>
        )}

        {/* Dynamic Sections from API */}
        {homeData.sections && Array.isArray(homeData.sections) && homeData.sections.length > 0 && (
          <>
            {homeData.sections
              .filter(section => section.content_type !== 'instructors' && section.content_type !== 'reels')
              .map((section, sectionIndex) => {
                // Create a unique section key that includes both ID and index to prevent duplicates
                const sectionKey = section.id ? `section-${section.id}-${sectionIndex}` : `section-${sectionIndex}`
                return (
              <AnimatedSection key={sectionKey} className="section py-20 bg-gray-900" animation="animate-fade-up">
                <div className="container mx-auto px-4">
                  {section.content_type === 'subjects' ? (
                    <div className="subjects-section-header mb-12">
                      <h2 className="subjects-main-title">
                        {section.title_ar || section.title || 'المواد الدراسية الموجودة'}
                      </h2>
                      {section.description ? (
                        <p className="subjects-description">{section.description}</p>
                      ) : (
                        <p className="subjects-description">
                          تتيح المنصة جميع المواد الدراسية المخصصة لك بأحدث مقرر وفقًا لهذا العام
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="section-header mb-12 text-center">
                      <h2 className="section-title text-3xl md:text-4xl font-extrabold text-white mb-4">
                        {section.title_ar || section.title}
                      </h2>
                      {section.content && section.content.length > (section.item_limit || 6) && (
                        <Link to={`/courses?section=${section.id}`} className="text-primary-blue hover:text-white font-bold transition-colors">
                          عرض الكل
                        </Link>
                      )}
                    </div>
                  )}
                  {section.description && section.content_type !== 'subjects' && (
                    <p className="text-gray-400 text-center mb-8 max-w-2xl mx-auto">{section.description}</p>
                  )}
                  {section.content_type === 'courses' && section.content && section.content.length > 0 && (
                    <div className="courses-swiper-wrapper">
                      <Swiper
                        modules={[Navigation, Pagination]}
                        spaceBetween={24}
                        slidesPerView={1}
                        breakpoints={{
                          640: { slidesPerView: 2 },
                          1024: { slidesPerView: 3 },
                          1280: { slidesPerView: 4 },
                        }}
                        navigation
                        pagination={{ clickable: true }}
                        className="courses-swiper"
                      >
                        {section.content.slice(0, section.item_limit || 6).map((course, courseIndex) => (
                          <SwiperSlide key={`${sectionKey}-course-${course.id != null ? course.id : courseIndex}-${courseIndex}`}>
                            <CourseCard course={course} />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                  )}
                           {section.content_type === 'subjects' && section.content && section.content.length > 0 && (
                             <div className="subjects-section-wrapper">
                               <Swiper
                                 modules={[Navigation, Pagination]}
                                 spaceBetween={24}
                                 slidesPerView={1}
                                 breakpoints={{
                                   640: { slidesPerView: 2 },
                                   1024: { slidesPerView: 3 },
                                 }}
                                 navigation
                                 className="subjects-swiper"
                               >
                                 {section.content.slice(0, section.item_limit || 6).map((subject, index) => {
                                   const SubjectIcon = getSubjectIcon(subject.name)
                                   const colorScheme = subjectColors[index % subjectColors.length]
                                   const metadata = getSubjectMetadata(subject)
                                   const hasImage = subject.icon || subject.image
                                   // Get instructor count (if available)
                                   const instructorCount = subject.instructors_count || subject.instructors?.length || subject.instructor_count || 0
                                   
                                   return (
                                     <SwiperSlide key={`${sectionKey}-subject-${subject.id != null ? subject.id : index}-${index}`}>
                                       <Link
                                         to={`/courses?subject=${subject.id}`}
                                         className="subject-card"
                                       >
                                         <div className="subject-icon-wrapper">
                                           {hasImage ? (
                                             <img 
                                               src={getStorageUrl(subject.icon || subject.image)} 
                                               alt={subject.name} 
                                               className="subject-icon-image" 
                                             />
                                           ) : (
                                             <div className="subject-icon-bg" style={{ background: colorScheme.bg }}>
                                               <SubjectIcon className="subject-icon" />
                                             </div>
                                           )}
                                         </div>
                                         <div className="subject-content">
                                           <h3 className="subject-title">{subject.name}</h3>
                                           <p className="subject-description">
                                             {metadata.university || metadata.college || subject.description || `منصة عافر`}
                                           </p>
                                           {instructorCount > 0 && (
                                             <div className="subject-status">
                                               <span className="subject-instructor-count">({instructorCount} مدرس)</span>
                                               <span className="status-dot"></span>
                                               <span className="status-text">موجود</span>
                                             </div>
                                           )}
                                         </div>
                                       </Link>
                                     </SwiperSlide>
                                   )
                                 })}
                               </Swiper>
                               {section.content.length > (section.item_limit || 6) && (
                                 <div className="subjects-navigation">
                                   <Link to={`/courses?section=${section.id}`} className="view-all-btn">
                                     عرض الكل
                                   </Link>
                                 </div>
                               )}
                             </div>
                           )}
                </div>
              </AnimatedSection>
              )
            })}
          </>
        )}

        {/* Instructors Section */}
        {instructors.length > 0 && (
          <section 
            ref={instructorsRef}
            className={`py-20 bg-gray-900 ${isInstructorsVisible ? 'animate-fade-up' : 'opacity-0'}`}
          >
            <div className="container mx-auto px-4">
              {/* Section Heading */}
              <div className="mb-12 text-center">
                <h2 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4"> أفضل المحاضرين المتخصصين</h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">المنصة تحتوي على نخبة من أفضل المدرسين المتخصصين في جميع المواد. يمكنك الاشتراك معهم وشراء الكورسات الخاصة بهم.</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 stagger-children">
                {instructors.map((instructor, instructorIndex) => (
                  <div
                    key={instructor.id != null ? `instructor-${instructor.id}-${instructorIndex}` : `instructor-${instructorIndex}`}
                    className="text-center p-6 bg-gray-800 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-700 hover:border-primary-blue flex flex-col items-center"
                  >
                    <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-primary-blue shadow-lg">
                      {instructor.avatar ? (
                        <img src={getStorageUrl(instructor.avatar)} alt={instructor.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary-blue to-accent-orange flex items-center justify-center text-white text-4xl font-bold">
                          {instructor.name?.[0]?.toUpperCase() || 'I'}
                        </div>
                      )}
                    </div>
                    <h4 className="text-xl font-bold text-white mb-1">{instructor.name}</h4>
                    <p className="text-primary-blue font-medium text-sm mb-4">{instructor.bio || instructor.title || 'خبير في التعليم'}</p>
                    <Link 
                      to={`/instructors/${instructor.id || instructorIndex}`}
                      className="inline-flex items-center justify-center gap-2 px-6 py-2 rounded-full font-bold transition-all duration-300 transform hover:-translate-y-1 active:scale-95 text-sm text-white shadow-lg hover:shadow-green-500/30"
                      style={{ backgroundColor: '#10b981' }}
                    >
                      اشتراك
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Reels Section */}
        {allReels.length > 0 && (
          <section 
            ref={reelsRef}
            className={`py-20 bg-gray-800 ${isReelsVisible ? 'animate-zoom-in' : 'section-waiting'}`}
          >
            <div className="container mx-auto px-4">
              {/* Section Heading */}
              <div className="mb-12 text-center">
                <h2 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">مقاطع عافر القصيرة (Reels)</h2>
                <p className="text-gray-400 text-lg">تعليم ممتع وسريع</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                {allReels.slice(0, 5).map((reel, reelIndex) => (
                  <Link
                    key={reel.id != null ? `reel-${reel.id}-${reelIndex}` : `reel-${reelIndex}`}
                    to={`/reels`}
                    className="relative block h-96 w-full rounded-2xl overflow-hidden shadow-xl group transition-all duration-300 transform hover:-translate-y-1"
                  >
                    <img
                      src={getStorageUrl(reel.thumbnail || reel.image || reel.url)}
                      alt={reel.title || reel.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent p-4 flex flex-col justify-end">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-300 group-hover:bg-accent-orange/90 group-hover:scale-110">
                          <FiPlay className="w-7 h-7" />
                        </div>
                      </div>
                      <h3 className="text-white text-lg font-bold line-clamp-2 mb-2">{reel.title || reel.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-200">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <polygon points="10 8 16 12 10 16 10 8"/>
                        </svg>
                        <span>{reel.views_count || '0'} مشاهدة</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              
              <div className="mt-12 text-center">
                <Link to="/reels">
                  <button className="inline-flex items-center justify-center gap-2 px-10 py-3 rounded-full font-bold transition-all duration-300 transform hover:-translate-y-1 active:scale-95 text-sm md:text-base bg-accent-orange text-white hover:bg-[#d98e15] shadow-lg hover:shadow-orange-500/30">
                    شاهد المزيد من المقاطع
                  </button>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Entertainment Materials Section */}
        {homeData.entertainment_materials && homeData.entertainment_materials.length > 0 && (
          <section 
            ref={entertainmentRef}
            className={`py-20 bg-gray-800 ${isEntertainmentVisible ? 'animate-zoom-in' : 'section-waiting'}`}
          >
            <div className="container mx-auto px-4">
              {/* Section Heading */}
              <div className="mb-12 text-center">
                <h2 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">مواد ترفيهية</h2>
                <p className="text-gray-400 text-lg">محتوى ترفيهي وتعليمي ممتع</p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
                {homeData.entertainment_materials.slice(0, 8).map((item, itemIndex) => (
                  <Link
                    key={item.id != null ? `entertainment-${item.id}-${itemIndex}` : `entertainment-${itemIndex}`}
                    to={item.url || '/reels'}
                    className="relative block h-80 w-full rounded-2xl overflow-hidden shadow-xl group transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl"
                  >
                    <img
                      src={getStorageUrl(item.image || item.thumbnail)}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4 flex flex-col justify-end">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-all duration-300 group-hover:bg-accent-orange/90 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-accent-orange/50">
                          <FiPlay className="w-8 h-8 ml-1" />
                        </div>
                      </div>
                      <div className="relative z-10">
                        <h3 className="text-white text-lg font-bold line-clamp-2 mb-2 group-hover:text-accent-orange transition-colors">
                          {item.title}
                        </h3>
                        {item.description && (
                          <p className="text-gray-300 text-sm line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Shine effect on hover */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                    </div>
                  </Link>
                ))}
              </div>
              
              {homeData.entertainment_materials.length > 8 && (
                <div className="mt-12 text-center">
                  <Link to="/reels">
                    <button className="inline-flex items-center justify-center gap-2 px-10 py-3 rounded-full font-bold transition-all duration-300 transform hover:-translate-y-1 active:scale-95 text-sm md:text-base bg-accent-orange text-white hover:bg-[#d98e15] shadow-lg hover:shadow-orange-500/30">
                      شاهد المزيد من المواد الترفيهية
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m12 19-7-7 7-7"/>
                        <path d="M19 12H5"/>
                      </svg>
                    </button>
                  </Link>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Testimonials Section */}
        {(homeData.reviews || homeData.testimonials || homeData.success_stories) && (homeData.reviews?.length > 0 || homeData.testimonials?.length > 0 || homeData.success_stories?.length > 0) && (
          <section 
            ref={storiesRef}
            className={`py-20 bg-gray-900 ${isStoriesVisible ? 'animate-fade-left' : 'section-waiting'}`}
          >
            <div className="container mx-auto px-4">
              {/* Section Heading */}
              <div className="mb-12 text-center">
                <h2 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">آراء وشهادات حول المنصة</h2>
                <p className="text-gray-400 text-lg">ماذا يقول طلابنا؟</p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8 stagger-children">
                {(homeData.reviews || homeData.testimonials || homeData.success_stories || []).slice(0, 3).map((review, index) => (
                  <div
                    key={review.id != null ? `testimonial-${review.id}-${index}` : `testimonial-${index}`}
                    className="bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-700 flex flex-col h-full hover:shadow-xl transition-shadow"
                  >
                    <div className="flex items-center gap-2 text-amber-500 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg key={star} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                        </svg>
                      ))}
                    </div>
                    <p className="text-gray-300 italic text-base mb-4 flex-grow line-clamp-4 leading-relaxed">
                      "{review.comment || review.review || review.testimonial || review.story}"
                    </p>
                    <div className="flex items-center gap-4 border-t border-gray-700 pt-4 mt-auto">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gray-700">
                        {review.user?.avatar ? (
                          <img src={getStorageUrl(review.user.avatar)} alt={review.user.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 font-bold">
                            {(review.user?.name || review.learner_name || review.author_name || 'م')[0]}
                          </div>
                        )}
                      </div>
                      <span className="font-bold text-white">
                        {review.user?.name || review.learner_name || review.author_name || 'متعلم'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Mobile App Section */}
        <section 
          ref={appDownloadRef}
          className={`py-20 overflow-hidden ${isAppDownloadVisible ? 'animate-fade-right' : 'section-waiting'}`}
        >
          <div className="container mx-auto px-4">
            <div className="bg-primary-blue rounded-3xl p-8 md:p-16 relative overflow-hidden flex flex-col lg:flex-row items-center gap-12 text-white">
              {/* Circles Background */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-orange/20 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl"></div>

              <div className="lg:w-1/2 relative z-10 text-center lg:text-right">
                <span className="inline-block px-4 py-1 bg-accent-orange text-white text-xs font-bold rounded-full mb-6">متوفر</span>
                <h2 className="text-3xl md:text-5xl font-bold mb-6">تعلم في أي وقت <br/>ومن أي مكان</h2>
                <p className="text-blue-100 text-lg mb-8 leading-relaxed">
                  حمل تطبيق عافر الآن واستمتع بتجربة تعليمية متكاملة على هاتفك. شاهد الدروس، حل الاختبارات، وتواصل مع مدربك بسهولة.
                </p>
                <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                  {/* App Store Button */}
                  <a
                    href="https://apps.apple.com/app/3afer"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-black text-white px-6 py-3 rounded-xl flex items-center gap-3 hover:bg-gray-900 transition-colors"
                  >
                    <div className="text-2xl"><i className="fab fa-apple"></i></div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider">Download on the</div>
                      <div className="font-bold text-sm leading-none">App Store</div>
                    </div>
                  </a>
                  {/* Google Play Button */}
                  <a
                    href="https://play.google.com/store/apps/details?id=com.3afer.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-black text-white px-6 py-3 rounded-xl flex items-center gap-3 hover:bg-gray-900 transition-colors"
                  >
                    <div className="text-2xl"><i className="fab fa-google-play"></i></div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider">GET IT ON</div>
                      <div className="font-bold text-sm leading-none">Google Play</div>
                    </div>
                  </a>
                </div>
              </div>

              <div className="lg:w-1/2 relative z-10 flex justify-center phone-frame-container">
                <div className="phone-glow"></div>
                <div className={`relative w-64 md:w-80 border-8 border-gray-900 rounded-[3rem] shadow-2xl bg-white overflow-hidden phone-frame ${phoneActiveSlide === 0 ? 'phone-tilt-left' : 'phone-tilt-right'}`}>
                  <div className="bg-gray-800 h-6 w-32 mx-auto rounded-b-xl absolute top-0 left-0 right-0 z-20"></div>
                  <img src="app.jpeg" alt="App Screen" className="w-full h-full object-cover" />
                  
                  {/* Slide 1 Content */}
                  <div className={`absolute inset-0 bg-gradient-to-t from-primary-blue/90 to-transparent flex flex-col justify-end p-6 text-white text-center pb-12 phone-screen-content ${phoneActiveSlide === 0 ? 'slide-active' : 'slide-exit'}`}>
                    <div className="font-bold text-xl mb-2">عافر</div>
                    <p className="text-sm opacity-90">منصتك التعليمية الأولى</p>
                    <button className="mt-4 bg-accent-orange text-white py-2 rounded-lg font-bold text-sm shadow-lg">ابدأ التعلم</button>
                  </div>

                  {/* Slide 2 Content */}
                  <div className={`absolute inset-0 bg-gradient-to-t from-primary-blue/90 to-transparent flex flex-col justify-end p-6 text-white text-center pb-12 phone-screen-content ${phoneActiveSlide === 1 ? 'slide-active' : 'slide-exit'}`}>
                    <div className="font-bold text-xl mb-2">تعلم بذكاء</div>
                    <p className="text-sm opacity-90">تواصل مع مدرسك بسهولة</p>
                    <button className="mt-4 bg-accent-orange text-white py-2 rounded-lg font-bold text-sm shadow-lg">حمل التطبيق</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Eduvalu Section */}
        <section ref={whyRef} className={`py-20 bg-gray-800 ${isWhyVisible ? 'animate-scale-up' : 'section-waiting'}`}>
          <div className="container mx-auto px-4">
            {/* Section Heading */}
            <div className="mb-12 text-center">
              <h2 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">لماذا منصة عافر؟</h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">اكتشف المميزات التي تجعل منصتنا الخيار الأفضل للطلاب والمعلمين.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
              {/* Feature Card 1 */}
              <div className="text-center p-6 rounded-2xl bg-gray-900 border border-gray-700 hover:border-primary-blue transition-all duration-300 hover:shadow-lg hover:shadow-primary-blue/20 group">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-blue/20 flex items-center justify-center text-primary-blue group-hover:scale-110 transition-transform duration-300">
                  <FiUsers className="w-8 h-8" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">أفضل المدرسين</h4>
                <p className="text-gray-400 text-sm leading-relaxed">نخبة من أفضل المدرسين المتخصصين في جميع المواد الدراسية.</p>
              </div>
              {/* Feature Card 2 */}
              <div className="text-center p-6 rounded-2xl bg-gray-900 border border-gray-700 hover:border-primary-blue transition-all duration-300 hover:shadow-lg hover:shadow-primary-blue/20 group">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-blue/20 flex items-center justify-center text-primary-blue group-hover:scale-110 transition-transform duration-300">
                  <FiBookOpen className="w-8 h-8" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">محتوى تعليمي متكامل</h4>
                <p className="text-gray-400 text-sm leading-relaxed">كورسات وكتب وامتحانات شاملة تغطي جميع احتياجات الطالب.</p>
              </div>
              {/* Feature Card 3 */}
              <div className="text-center p-6 rounded-2xl bg-gray-900 border border-gray-700 hover:border-primary-blue transition-all duration-300 hover:shadow-lg hover:shadow-primary-blue/20 group">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-blue/20 flex items-center justify-center text-primary-blue group-hover:scale-110 transition-transform duration-300">
                  <FiAward className="w-8 h-8" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">نتائج متميزة</h4>
                <p className="text-gray-400 text-sm leading-relaxed">مساعدة الطلاب على تحقيق التفوق والنجاح في جميع المواد.</p>
              </div>
              {/* Feature Card 4 */}
              <div className="text-center p-6 rounded-2xl bg-gray-900 border border-gray-700 hover:border-primary-blue transition-all duration-300 hover:shadow-lg hover:shadow-primary-blue/20 group">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-blue/20 flex items-center justify-center text-primary-blue group-hover:scale-110 transition-transform duration-300">
                  <FiZap className="w-8 h-8" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">تجربة تعليمية حديثة</h4>
                <p className="text-gray-400 text-sm leading-relaxed">منصة تفاعلية وسهلة الاستخدام مع أحدث التقنيات التعليمية.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section ref={faqRef} className={`py-20 bg-gray-900 ${isFaqVisible ? 'animate-fade-up' : 'section-waiting'}`}>
          <div className="container mx-auto px-4">
            <div className="mb-12 text-center">
              <h2 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">الأسئلة الشائعة</h2>
              <p className="text-gray-400 text-lg">إجابات على أكثر الأسئلة شيوعًا حول المنصة والخدمات.</p>
            </div>
            
            <div className="max-w-3xl mx-auto space-y-4 stagger-children">
              {kbLoading ? (
                <div className="text-center py-8">
                  <Loading />
                </div>
              ) : knowledgeBaseQuestions.length > 0 ? (
                knowledgeBaseQuestions.map((kbQuestion) => {
                  // API returns localized question and answer based on locale parameter
                  const question = kbQuestion.question
                  const answer = kbQuestion.answer
                  
                  // Skip if question or answer is empty
                  if (!question || !answer) return null
                  
                  return (
                    <FAQItem 
                      key={kbQuestion.id} 
                      question={question} 
                      answer={answer} 
                    />
                  )
                })
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <p>لا توجد أسئلة متاحة حاليًا.</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Ready for Success CTA */}
        <section className="py-20 bg-gray-800">
          <div className="container mx-auto px-4">
            <div className="bg-gradient-to-r from-primary-blue to-primary-blue/80 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 -translate-x-1/2 blur-3xl"></div>
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 translate-x-1/2 blur-3xl"></div>
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">مستعد للنجاح؟</h2>
                <p className="text-gray-200 text-lg mb-8 max-w-2xl mx-auto">
                  منصة عافر هي منصة تهدف إلي تطوير العملية التعليمية لمساعدة الطالب في تحقق النجاح في جميع المواد والتفوق بها وذلك من خلال الخدمات التي نقدمها.
                </p>
                {!isAuthenticated && (
                  <Link to="/register">
                    <button 
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold transition-all duration-300 transform hover:-translate-y-1 active:scale-95 text-white shadow-lg hover:shadow-green-500/30"
                      style={{ backgroundColor: '#10b981' }}
                    >
                      انشئ حساب الان
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m12 19-7-7 7-7"/>
                        <path d="M19 12H5"/>
                      </svg>
                    </button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      
      {/* Video Modal */}
      <YouTubeModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoUrl="https://www.youtube.com/watch?v=LXb3EKWsInQ" // Replace with actual video URL
      />
    </div>
  )
}

export default Home

